import React from "react";
import {
  Box,
  Button,
  Card,
  type Color,
  Dialog,
  DialogPopup,
  DialogTitle,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  Icon,
  type IconName,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ScrollArea,
  Separator,
  Spinner,
  Text,
  useElementSize,
  useIsMobile,
} from "@salestrak/ui";

import { MANAGE_DIALOG_SURFACE } from "#app/lib/constants/manage-dialog";
import { ConnectionConfigView, NEW_CONNECTION } from "./connection-config-view";
import {
  ConfigCrumb,
  type ConfigCrumbSegment,
  IconChip,
} from "./integrations-config-primitives";
import {
  filterProviders,
  type IntegrationCategory,
  type IntegrationProvider,
} from "./integrations-data";
import { useIntegrationCatalog } from "./use-integration-catalog";

const DEFAULT_CATEGORY_ID = "featured";
const SIDEBAR_WIDTH = 240;
// Desktop card footprint.
const CARD_WIDTH = 360;
const CARD_HEIGHT = 200;
// Card gap (Box gap="md") and the content's horizontal padding (paddingX="lg").
const CARD_GAP = 16;
const CONTENT_PAD_X = 24;
// Centered config-panel column width (header + tabs + option rows).
const CONFIG_MAX_WIDTH = 760;

// Semantic text color for a status label; undefined → muted.
type StatusColor = "success" | "info" | "warning";

// View-model the card renders from.
// The whole card is clickable (opens the config panel); there's no separate
// action button.
type CardModel = {
  id: string;
  icon: IconName;
  /** Icon tint (provider accent color); undefined → muted. */
  iconColor?: Color | undefined;
  /** Full-color brand logo tile; renders instead of `icon` when present. */
  logo?: string | undefined;
  name: string;
  description: string;
  footnote: string;
  statusLabel: string;
  statusColor?: StatusColor | undefined;
  /** Whole-card click → open the config panel. */
  onClick?: (() => void) | undefined;
};

/**
 * The card's status line: what this provider's connections are DOING, not just
 * how many there are.
 *
 * A bare count answered the wrong question. Three connections reads as healthy
 * whether they are three working ones, two switched off, or one whose app was
 * uninstalled this morning — and the last is the case someone needs to act on,
 * which is exactly the one a total hid.
 *
 * Only non-zero counts render, so a healthy provider still reads as one short
 * phrase rather than a row of zeros. No connections at all keeps "Available",
 * which is a different statement — nothing is wrong, there is simply nothing
 * set up.
 *
 * Exported for its unit test: this is the only place the states are counted, and
 * it was previously unreachable inside this module.
 */
export function connectionStatus(connections: IntegrationProvider["connections"]): {
  label: string;
  color: StatusColor;
} {
  if (connections.length === 0) return { label: "Available", color: "info" };

  const attention = connections.filter((c) => c.needsAttention).length;
  const disabled = connections.filter((c) => !c.active && !c.needsAttention).length;
  // Anything neither broken nor switched off. Derived by subtraction rather than
  // filtered again, so the three counts always sum to the total and no
  // connection can be reported twice or not at all.
  const active = connections.length - attention - disabled;

  const parts: string[] = [];
  if (active > 0) parts.push(`${active} active`);
  if (disabled > 0) parts.push(`${disabled} disabled`);
  if (attention > 0) parts.push(`${attention} needs attention`);

  return {
    // Worst state wins the colour: one broken connection among nine healthy ones
    // is the thing worth looking at, so the card must not read as green.
    color: attention > 0 ? "warning" : active > 0 ? "success" : "info",
    label: parts.join(" · "),
  };
}

// Multi-config: the card's status reflects a provider's saved connections.
// Clicking the card opens the config panel.
function providerCard(
  p: IntegrationProvider,
  onOpen?: (p: IntegrationProvider) => void,
): CardModel {
  const status = connectionStatus(p.connections);
  return {
    id: p.id,
    icon: p.icon,
    iconColor: p.color,
    logo: p.logo,
    name: p.name,
    description: p.description,
    footnote: p.footnote ?? "",
    statusLabel: status.label,
    statusColor: status.color,
    onClick: onOpen ? () => onOpen(p) : undefined,
  };
}

function matchesQuery(card: CardModel, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    card.name.toLowerCase().includes(q) ||
    card.description.toLowerCase().includes(q)
  );
}

/** One marketplace card. `fixed` gives the desktop 360×200 footprint; fluid
 * fills its column (mobile). Clickable when the model supplies `onClick`. */
function MarketplaceCard({ card, fixed }: { card: CardModel; fixed?: boolean }) {
  return (
    <Card
      cursor={card.onClick ? "pointer" : undefined}
      fill={fixed ? undefined : "width"}
      gap="md"
      height={fixed ? CARD_HEIGHT : undefined}
      hoverBackground={card.onClick ? true : undefined}
      justify="between"
      padding="md"
      radius="lg"
      width={fixed ? CARD_WIDTH : undefined}
      onClick={card.onClick}
    >
      <Box align="start" direction="row" justify="between">
        <IconChip color={card.iconColor} logo={card.logo} name={card.icon} />
        <Text size="xs" variant={card.statusColor ?? "muted"} weight="medium">
          {card.statusLabel}
        </Text>
      </Box>
      <Box gap="2xs">
        <Text weight="semibold">{card.name}</Text>
        <Text size="sm" truncate={2} variant="muted">
          {card.description}
        </Text>
      </Box>
      <Text size="xs" variant="muted">
        {card.footnote}
      </Text>
    </Card>
  );
}

/** Card grid + empty state. Desktop wraps fixed-size cards; mobile stacks
 * full-width cards. */
function CardGrid({ cards, fixed }: { cards: CardModel[]; fixed?: boolean }) {
  if (cards.length === 0) {
    return (
      <Text size="sm" variant="muted">
        Nothing here yet.
      </Text>
    );
  }
  if (fixed) {
    return (
      <Box direction="row" fill="width" gap="md" wrap>
        {cards.map((card, index) => (
          <MarketplaceCard key={`${card.id}-${index}`} card={card} fixed />
        ))}
      </Box>
    );
  }
  return (
    <Box fill="width" gap="md">
      {cards.map((card, index) => (
        <MarketplaceCard key={`${card.id}-${index}`} card={card} />
      ))}
    </Box>
  );
}

/** One category row in the left nav — the same ghost/secondary Button row the
 * admin console's sidebar uses. */
function CategoryRow({
  category,
  isActive,
  onClick,
}: {
  category: IntegrationCategory;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      aria-current={isActive ? "page" : undefined}
      className="w-full justify-start"
      size="sm"
      variant={isActive ? "secondary" : "ghost"}
      onClick={onClick}
    >
      <Icon color={isActive ? undefined : "muted"} name={category.icon} size="sm" />
      <Text size="sm" weight={isActive ? "medium" : "normal"}>
        {category.label}
      </Text>
    </Button>
  );
}

/** Left-nav: the category filters for the provider grid. */
function CategoryNav({
  categories,
  hasProviders,
  selectedId,
  onSelectCategory,
}: {
  categories: IntegrationCategory[];
  /** Whether any provider exists — renders nothing if not. */
  hasProviders: boolean;
  selectedId: string;
  onSelectCategory: (id: string) => void;
}) {
  return (
    <ScrollArea>
      <Box gap="2xs" paddingX="sm" paddingY="sm">
        {hasProviders
          ? categories.map((category, index) => (
              <CategoryRow
                key={`${category.id}-${index}`}
                category={category}
                isActive={category.id === selectedId}
                onClick={() => onSelectCategory(category.id)}
              />
            ))
          : null}
      </Box>
    </ScrollArea>
  );
}

/** Shared search input. */
function IntegrationSearch({
  query,
  onChange,
}: {
  query: string;
  onChange: (q: string) => void;
}) {
  return (
    <InputGroup>
      <InputGroupAddon>
        <Icon name="search" />
      </InputGroupAddon>
      <InputGroupInput
        placeholder="Search integrations..."
        type="search"
        value={query}
        onValueChange={onChange}
      />
    </InputGroup>
  );
}

/** Empty state when the marketplace has no providers. */
function ComingSoonPanel() {
  return (
    <Box align="center" fill="width" gap="xs" justify="center" paddingY="2xl">
      <IconChip name="package" size="lg" />
      <Text size="lg" weight="bold">
        Coming soon
      </Text>
      <Text size="sm" variant="muted">
        Integrations will appear here once they're available.
      </Text>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Dialog — desktop two-pane (nav + card grid), mobile drawer (search + grid).
// ---------------------------------------------------------------------------

export function IntegrationsManageDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  // Marketplace data from the Core API (code registry); fetch once open.
  const { categories, providers, initialLoading } = useIntegrationCatalog({
    skip: !open,
  });
  const hasProviders = providers.length > 0;
  const isEmpty = !initialLoading && !hasProviders;
  const [selectedId, setSelectedId] = React.useState(DEFAULT_CATEGORY_ID);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<IntegrationProvider | null>(null);
  // Which of the selected provider's connections the panel has open (or
  // NEW_CONNECTION). Lifted out of `ConnectionConfigView` so the breadcrumb owns
  // that level of navigation instead of an in-panel back link. `null` = the
  // provider's connections list.
  const [connectionId, setConnectionId] = React.useState<string | null>(null);
  // `selected` holds a snapshot of the provider at open time; re-resolve the
  // live one from the (re-fetched) catalog so edits — rename, status, delete —
  // reflect immediately without reopening. Falls back to the snapshot.
  const selectedProvider = selected
    ? providers.find((p) => p.id === selected.id) ?? selected
    : null;
  // Content pane width (ResizeObserver) — sizes the fixed-card grid columns and
  // the centered config column.
  const { ref: paneRef, size: paneSize } = useElementSize<HTMLDivElement>();
  const paneWidth = Math.floor(paneSize.width);

  // Leaving the config panel always drops the open connection with it, so the
  // next provider never inherits the previous one's detail level.
  const clearSelection = React.useCallback(() => {
    setSelected(null);
    setConnectionId(null);
  }, []);

  React.useEffect(() => {
    if (!open) {
      setSelectedId(DEFAULT_CATEGORY_ID);
      setQuery("");
      clearSelection();
    }
  }, [open, clearSelection]);

  const handleSelectCategory = React.useCallback(
    (id: string) => {
      setSelectedId(id);
      clearSelection();
    },
    [clearSelection],
  );

  // Clicking a provider card opens the config panel.
  const handleProviderAction = React.useCallback((p: IntegrationProvider) => {
    setSelected(p);
    setConnectionId(null);
  }, []);

  // Page segment for the open connection — null while the connections list is
  // showing, and also when the open connection has just left the catalog (a
  // delete), which `ConnectionConfigView` resolves by resetting to the list.
  // A persisted connection's detail view is the one panel that manages its own
  // scrolling (fixed header + tab strip, scrolling tab content), so the desktop
  // pane hands it a bounded height instead of wrapping it in a page-level
  // scroll area. The connections list and the new-connection form scroll as a
  // page like every other panel.
  const connectionDetailOpen =
    selectedProvider !== null &&
    connectionId !== null &&
    connectionId !== NEW_CONNECTION;

  const connectionCrumb: ConfigCrumbSegment | null = React.useMemo(() => {
    if (connectionId === null) return null;
    if (connectionId === NEW_CONNECTION) return { label: "New connection", icon: "plus" };
    const name = selectedProvider?.connections.find((c) => c.id === connectionId)?.name;
    return name ? { label: name, icon: "plug" } : null;
  }, [connectionId, selectedProvider]);

  // Trail for the provider panel: the grid the card was opened from → the
  // provider → the open connection. The provider segment is a link only while a
  // connection is open, since that is the only time it navigates anywhere.
  const providerTrail = (
    provider: IntegrationProvider,
    rootLabel: string,
    rootIcon: IconName,
  ): ConfigCrumbSegment[] => [
    { label: rootLabel, icon: rootIcon, onClick: clearSelection },
    {
      label: provider.name,
      icon: provider.icon,
      logo: provider.logo,
      onClick: connectionCrumb ? () => setConnectionId(null) : undefined,
    },
    ...(connectionCrumb ? [connectionCrumb] : []),
  ];

  if (isMobile) {
    const mobileCards = providers.map((p) => providerCard(p, handleProviderAction));
    const visible = mobileCards.filter((c) => matchesQuery(c, query));
    return (
      <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
        <DrawerPopup showBar>
          <DrawerHeader>
            <DrawerTitle>Integrations</DrawerTitle>
          </DrawerHeader>
          {selectedProvider ? (
            <Box paddingBottom="sm" paddingX="md">
              <ConfigCrumb
                trail={providerTrail(selectedProvider, "All Integrations", "layout-grid")}
              />
            </Box>
          ) : isEmpty ? null : (
            <Box paddingBottom="sm" paddingX="md">
              <IntegrationSearch query={query} onChange={setQuery} />
            </Box>
          )}

          <DrawerPanel>
            <Box gap="md">
              {selectedProvider ? (
                <ConnectionConfigView
                  activeConnectionId={connectionId}
                  provider={selectedProvider}
                  onActiveConnectionIdChange={setConnectionId}
                />
              ) : isEmpty ? (
                <ComingSoonPanel />
              ) : initialLoading ? (
                <Box align="center" justify="center" paddingY="2xl">
                  <Spinner size="lg" />
                </Box>
              ) : (
                <CardGrid cards={visible} />
              )}
            </Box>
          </DrawerPanel>
        </DrawerPopup>
      </Drawer>
    );
  }

  // The "Featured" bucket is only present when something is featured (server
  // omits it otherwise); the default lands there when available, else falls
  // back to "All Integrations" so we never show an empty Featured grid.
  const featuredAvailable = categories.some((c) => c.id === "featured");
  const effectiveSelectedId =
    selectedId === "featured" && !featuredAvailable ? "all-integrations" : selectedId;
  const activeCategory = categories.find((c) => c.id === effectiveSelectedId);
  const activeLabel = activeCategory?.label ?? "Featured";
  const crumbIcon: IconName = activeCategory?.icon ?? "layout-grid";
  const cards = filterProviders(effectiveSelectedId, providers).map((p) =>
    providerCard(p, handleProviderAction),
  );
  const visible = cards.filter((c) => matchesQuery(c, query));

  const fitColumns = paneWidth
    ? Math.max(
        1,
        Math.floor(
          (paneWidth - CONTENT_PAD_X * 2 + CARD_GAP) / (CARD_WIDTH + CARD_GAP),
        ),
      )
    : 0;
  const gridWidth = fitColumns
    ? fitColumns * CARD_WIDTH + (fitColumns - 1) * CARD_GAP
    : undefined;
  const configWidth = paneWidth
    ? Math.min(CONFIG_MAX_WIDTH, paneWidth - CONTENT_PAD_X * 2)
    : undefined;
  // The content column, rendered inside the page scroll area normally and
  // standalone (bounded, so the panel scrolls internally) for a connection
  // detail. In that mode the pane also drops its own horizontal padding and
  // centering — the panel centers each of its regions instead, so its scroll
  // container spans the full pane and its scrollbar lands on the pane edge.
  const pane = (
    <Box
      ref={paneRef}
      align={connectionDetailOpen ? undefined : "center"}
      // A flex CONTAINER, which is a separate thing from the flex ITEM `fill`
      // makes this. The detail panel bounds itself by resolving `flex-1` against
      // this Box, and only a flex parent offers that to resolve against — a bare
      // Box is `display: block`, which hands the panel an auto height that grows
      // to its content, and the tab body then has no height to scroll within.
      // `align` opts in on its own for the connections list; the detail branch
      // passes none, so the mode that needs the chain is the one that loses it.
      direction="column"
      fill={connectionDetailOpen ? true : "width"}
      // Flex children default to `min-height: auto`; without this the column
      // grows to its content instead of bounding the panel inside it.
      minHeight={connectionDetailOpen ? 0 : undefined}
      paddingBottom={connectionDetailOpen ? undefined : "md"}
      paddingTop="md"
      paddingX={connectionDetailOpen ? undefined : "lg"}
    >
      {connectionDetailOpen && selectedProvider ? (
        <ConnectionConfigView
          activeConnectionId={connectionId}
          contentWidth={configWidth}
          fillHeight
          provider={selectedProvider}
          onActiveConnectionIdChange={setConnectionId}
        />
      ) : selectedProvider ? (
        <Box fill={configWidth ? undefined : "width"} width={configWidth}>
          <ConnectionConfigView
            activeConnectionId={connectionId}
            provider={selectedProvider}
            onActiveConnectionIdChange={setConnectionId}
          />
        </Box>
      ) : initialLoading ? (
        <Box align="center" justify="center" paddingY="2xl">
          <Spinner size="lg" />
        </Box>
      ) : isEmpty ? (
        <ComingSoonPanel />
      ) : (
        <Box fill={gridWidth ? undefined : "width"} gap="md" width={gridWidth}>
          <Text size="lg" weight="bold">
            {activeLabel}
          </Text>
          <CardGrid cards={visible} fixed />
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* The shared manage-dialog surface, not `size="full"` — every workspace-
       * config dialog reads the same pair so they open at one size. `size="full"`
       * has no width cap, so this one alone kept growing with the monitor. */}
      <DialogPopup className={MANAGE_DIALOG_SURFACE}>
        <Box direction="row" fill minHeight={0}>
          {/* Left nav */}
          <Box
            border="right"
            direction="column"
            surface="sidebar"
            width={SIDEBAR_WIDTH}
          >
            <Box align="center" direction="row" gap="xs" paddingX="md" paddingY="sm">
              <Icon color="muted" name="layout-dashboard" size="sm" />
              <DialogTitle>Integrations</DialogTitle>
            </Box>
            <CategoryNav
              categories={categories}
              hasProviders={hasProviders}
              selectedId={effectiveSelectedId}
              onSelectCategory={handleSelectCategory}
            />
          </Box>

          {/* Content */}
          <Box direction="column" fill minWidth={0}>
            <Box
              align="center"
              direction="row"
              gap="md"
              justify="between"
              paddingLeft="md"
              paddingRight="2xl"
              paddingY="sm"
            >
              <Box align="center" direction="row" gap="2xs" shrink>
                {selectedProvider ? (
                  <ConfigCrumb
                    trail={providerTrail(selectedProvider, activeLabel, crumbIcon)}
                  />
                ) : (
                  <>
                    <Icon color="muted" name={crumbIcon} size="sm" />
                    <Text size="sm" truncate={1} weight="medium">
                      {activeLabel}
                    </Text>
                  </>
                )}
              </Box>
              {selectedProvider ? null : (
                <Box width={300}>
                  <IntegrationSearch query={query} onChange={setQuery} />
                </Box>
              )}
            </Box>
            <Separator />
            {connectionDetailOpen ? pane : <ScrollArea>{pane}</ScrollArea>}
          </Box>
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
